/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * `scrollIntoView` with a Chromium 52 fallback — see
 * docs/CHROMIUM-52-COMPATIBILITY.md (P2-2).
 *
 * `Element.scrollIntoView()` itself is ancient, but the **options dictionary**
 * overload (`{ behavior, block }`) is Chrome 61. Chromium 52 only has the
 * boolean overload, so it coerces the object with `ToBoolean` → `true` →
 * `alignToTop`. That means `block: "center"` and `block: "nearest"` are
 * *silently ignored* and the focused element jumps to the top of its scroll
 * container. On a D-pad TV UI that reads as the focus ring leaping around, and
 * because the call still "works" no error ever surfaces.
 *
 * On hosts that do support the dictionary this delegates straight to the native
 * implementation. On older ones — and on every host when `margin` is asked for,
 * which nothing native carries — it resolves the scroll container itself,
 * computes the target `scrollTop` for the requested alignment, and either sets
 * it or animates it with `requestAnimationFrame`.
 *
 * The animation also restores what **P2-8** gave up: `scroll-behavior: smooth`
 * was removed from `MyHubScreen.scss` because Chromium 52 ignores it, which cost
 * desktop its smooth programmatic scrolling. Routing every call through here
 * gives both the same behaviour again — natively above Chrome 61, by animation
 * below it.
 *
 * Vertical only, deliberately: `block` is the only axis any call site uses, and
 * an untested `inline` branch would be worse than no branch. Add it when
 * something needs it.
 */

/** Vertical alignment of the element within its scroll container. */
export type TScrollBlock = "start" | "center" | "end" | "nearest";

/**
 * The supported subset of `ScrollIntoViewOptions`. Narrower than the DOM type on
 * purpose — see the note on the vertical-only limitation above.
 */
export interface IScrollIntoViewOptions {
  behavior?: "auto" | "smooth";
  block?: TScrollBlock;

  /**
   * Extra space to keep visible above and below the element, in CSS pixels —
   * what CSS `scroll-margin` expresses, which is Chrome 69 and so seventeen
   * major versions past this baseline.
   *
   * Needed whenever the thing being revealed paints outside its own box: a
   * focus ring drawn with `outline-offset` bleeds past the element, and every
   * alignment except `center` puts the element's border box flush against a
   * scrollport edge, which shaves that bleed off. `nearest` also stops
   * scrolling as soon as the *box* is inside the port, so without this the
   * ring can sit half out of view and the grid considers it done.
   *
   * A non-zero margin takes the manual path even where the native dictionary is
   * supported, since no native option carries it.
   */
  margin?: number;
}

/** Duration of the fallback smooth scroll, matching Blink's own feel. */
const SMOOTH_SCROLL_DURATION_MS: number = 300;

/**
 * In-flight fallback animations, keyed by scroll container, so a second
 * navigation cancels the first instead of fighting it. D-pad repeat outruns a
 * 300 ms animation easily, and two live loops writing the same `scrollTop`
 * produce visible stutter.
 *
 * Weak, so an unmounted container is not held alive by a cancelled frame id.
 */
const activeAnimations: WeakMap<Element, number> = new WeakMap();

/**
 * True when the host understands the options-dictionary overload.
 *
 * `scroll-behavior` (the CSS property) and `ScrollIntoViewOptions` both landed
 * in Chrome 61, so the presence of the former is a reliable proxy for the
 * latter — and unlike the dictionary itself it is directly detectable. Probing
 * `scrollIntoView` is not an option: the boolean overload accepts an object
 * without complaint, which is the whole problem.
 *
 * Computed per call rather than cached at module scope; it is one property
 * lookup, and a cached value would outlive the JSDOM document in tests.
 */
function supportsScrollIntoViewOptions(): boolean {
  if (typeof document === "undefined" || document.documentElement === null) {
    return false;
  }
  return "scrollBehavior" in document.documentElement.style;
}

/**
 * True for the document-level scroller, whose geometry has to be measured
 * differently — see `contentOffsetTop`.
 */
function isRootScroller(container: Element): boolean {
  return container === document.documentElement || container === document.body;
}

/**
 * Nearest ancestor that actually scrolls vertically, falling back to the
 * document element.
 *
 * Both halves of the test matter: `overflow-y` alone matches containers with
 * nothing to scroll, and overflowing content alone matches every ancestor of a
 * long page.
 */
function findScrollContainer(element: HTMLElement): HTMLElement {
  let node: HTMLElement | null = element.parentElement;

  while (
    node !== null &&
    node !== document.body &&
    node !== document.documentElement
  ) {
    const overflowY: string = window.getComputedStyle(node).overflowY;
    const scrolls: boolean =
      overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";

    if (scrolls && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }

  return document.documentElement;
}

/**
 * Distance from the top of the container's *scrollable content* to the top of
 * the element — i.e. the `scrollTop` that would put the element flush with the
 * container's top edge.
 *
 * The root scroller needs its own branch: `documentElement`'s bounding rect
 * already moves with the scroll position, so subtracting it would count
 * `scrollTop` twice. A scrolling `div`'s border box does not move when its
 * content scrolls, so there the rect difference is the right measure —
 * less `clientTop`, which is the border the rect includes and `scrollTop` does
 * not.
 */
function contentOffsetTop(
  element: HTMLElement,
  container: HTMLElement
): number {
  const elementTop: number = element.getBoundingClientRect().top;

  if (isRootScroller(container)) {
    return elementTop + container.scrollTop;
  }

  const containerTop: number = container.getBoundingClientRect().top;
  return elementTop - containerTop - container.clientTop + container.scrollTop;
}

/**
 * The `scrollTop` that lands the element at the requested alignment, clamped to
 * the container's scrollable range.
 *
 * `margin` is applied by growing the element's box by that much on both sides
 * before any alignment is worked out, which is how CSS `scroll-margin` behaves
 * and means every branch below — including `nearest`'s "is it already in view"
 * test — accounts for it without a special case. The final clamp keeps the
 * result inside the scroll range, so at the two ends of the scroll the margin
 * simply cannot be honoured; that is what the container's own padding is for.
 */
function targetScrollTop(
  element: HTMLElement,
  container: HTMLElement,
  block: TScrollBlock,
  margin: number
): number {
  const elementHeight: number =
    element.getBoundingClientRect().height + margin * 2;
  const viewportHeight: number = container.clientHeight;
  const offsetTop: number = contentOffsetTop(element, container) - margin;

  const alignStart: number = offsetTop;
  const alignEnd: number = offsetTop + elementHeight - viewportHeight;

  let target: number;

  if (block === "center") {
    target = offsetTop - (viewportHeight - elementHeight) / 2;
  } else if (block === "end") {
    target = alignEnd;
  } else if (block === "nearest") {
    // Whichever edge is out of view, scroll by the least amount that fixes it —
    // and if the element already fits, do not move at all.
    const visibleTop: number = container.scrollTop;
    const visibleBottom: number = visibleTop + viewportHeight;
    const elementBottom: number = offsetTop + elementHeight;

    if (offsetTop < visibleTop) {
      target = alignStart;
    } else if (elementBottom > visibleBottom) {
      target = alignEnd;
    } else {
      target = visibleTop;
    }
  } else {
    target = alignStart;
  }

  const maxScrollTop: number = container.scrollHeight - viewportHeight;
  return Math.max(0, Math.min(target, Math.max(0, maxScrollTop)));
}

/** Ease-out cubic — decelerating, like Blink's own smooth scroll. */
function easeOut(progress: number): number {
  return 1 - Math.pow(1 - progress, 3);
}

/** Stops any fallback animation currently driving this container. */
function cancelAnimation(container: Element): void {
  const frameId: number | undefined = activeAnimations.get(container);
  if (frameId !== undefined) {
    window.cancelAnimationFrame(frameId);
    activeAnimations.delete(container);
  }
}

/**
 * Animates `scrollTop` to `target` over `SMOOTH_SCROLL_DURATION_MS`.
 *
 * The elapsed time comes from the timestamp `requestAnimationFrame` passes its
 * callback, using the first frame as the origin, so no clock is read directly.
 */
function animateScrollTop(container: HTMLElement, target: number): void {
  const from: number = container.scrollTop;
  const distance: number = target - from;

  if (distance === 0) {
    return;
  }

  let startTime: number = -1;

  const step = (timestamp: number): void => {
    if (startTime < 0) {
      startTime = timestamp;
    }

    const elapsed: number = timestamp - startTime;
    const progress: number = Math.min(1, elapsed / SMOOTH_SCROLL_DURATION_MS);

    container.scrollTop = from + distance * easeOut(progress);

    if (progress < 1) {
      activeAnimations.set(container, window.requestAnimationFrame(step));
      return;
    }
    activeAnimations.delete(container);
  };

  activeAnimations.set(container, window.requestAnimationFrame(step));
}

/**
 * Scrolls `element` into view, honouring `block` on hosts that predate the
 * options-dictionary overload.
 *
 * Drop-in replacement for `element.scrollIntoView({ behavior, block })`.
 *
 * @param element - The element to reveal.
 * @param options - Alignment, behaviour and margin; defaults to
 *   `block: "start"`, an instant scroll and no margin, matching the DOM's own
 *   defaults.
 */
export function scrollIntoView(
  element: HTMLElement,
  options?: IScrollIntoViewOptions
): void {
  const resolved: IScrollIntoViewOptions =
    options !== undefined && options !== null ? options : {};
  const block: TScrollBlock =
    resolved.block !== undefined ? resolved.block : "start";
  const behavior: "auto" | "smooth" =
    resolved.behavior !== undefined ? resolved.behavior : "auto";
  const margin: number =
    resolved.margin !== undefined && resolved.margin > 0 ? resolved.margin : 0;

  // A margin has no native equivalent to delegate to, so it is the one option
  // that takes the manual path on every host rather than only on old ones.
  if (margin === 0 && supportsScrollIntoViewOptions()) {
    element.scrollIntoView({ behavior, block });
    return;
  }

  const container: HTMLElement = findScrollContainer(element);
  const target: number = targetScrollTop(element, container, block, margin);

  cancelAnimation(container);

  if (
    behavior === "smooth" &&
    typeof window.requestAnimationFrame === "function"
  ) {
    animateScrollTop(container, target);
    return;
  }

  container.scrollTop = target;
}
